/* eslint-disable max-lines-per-function */
import React, { useState, useEffect } from 'react'
import {
  SubmitHandler,
  UseFormGetValues,
  type UseFormReset,
  type UseFormResetField,
  type UseFormWatch,
} from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import notify from '~/utils/notify'
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
} from '@mui/material'
import LoopIcon from '@mui/icons-material/Loop'
import { useLang } from '~/hooks/useLang'
import Layout from '~/components/Layout'
import ButtonForm from '~/components/ButtonForm'
import CrudGeneratorFields from '../CrudGeneratorFields'
import { AxiosError, Method } from 'axios'
import ICrudEditParams from './interfaces/ICrudEditParams'
import { authFetch } from '~/services/fetch'
import { UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { formatDate } from '~/utils/format'
import InternalHeader from '../Layout/components/InternalHeader'
import InternalContent from '../Layout/components/InternalContent'
import { objectToSnakeCase } from '~/utils/format'
import type { State } from '@hookstate/core'

export type EditField = {
  readonly name: string
  readonly type: string
  readonly model?: string
  readonly placeholder?: string
  readonly method?: string
}
interface CrudEditProps {
  readonly model: string
  readonly fields: readonly EditField[]
  readonly register: UseFormRegister<any>
  readonly handleSubmit: any
  readonly control: any
  readonly setFormValue: UseFormSetValue<any>
  readonly messageSuccess: string
  readonly title: string
  readonly getValues: UseFormGetValues<any>
  readonly method?: Method
  readonly watch?: UseFormWatch<any>
  readonly resetField?: UseFormResetField<any>
  readonly disableAutoLoad?: boolean
}

export default function CrudEdit({
  method,
  model,
  fields,
  register,
  handleSubmit,
  control,
  setFormValue,
  messageSuccess,
  title,
  getValues,
  watch,
  resetField,
  disableAutoLoad = false,
}: CrudEditProps) {
  const { translate } = useLang()
  const { id } = useParams<ICrudEditParams>()
  const history = useHistory()
  const [processing, setProcessing] = useState<number>(0)

  useEffect(() => {
    if (disableAutoLoad) return

    authFetch({
      url: `/${model}/${id}`,
      method: 'GET',
    })
      .then((response) => {
        if (response.data) {
          const toSnake = (item: string) =>
            item.replace(/[A-Z]/g, (itemLow) => `_${itemLow.toLowerCase()}`)

          const getValueFromResponse = (resp: any, fieldName: string) => {
            if (resp[fieldName] !== undefined) return resp[fieldName]

            const snake = toSnake(fieldName)
            if (resp[snake] !== undefined) return resp[snake]

            const related = fieldName.replace(/Id$/, '')
            if (resp[related] !== undefined) {
              const rel = resp[related]
              return rel && typeof rel === 'object' ? rel.id ?? rel : rel
            }
            const relatedSnake = toSnake(related)
            if (resp[relatedSnake] !== undefined) {
              const rel = resp[relatedSnake]
              return rel && typeof rel === 'object' ? rel.id ?? rel : rel
            }

            return undefined
          }

          fields.forEach((params) => {
            const value = getValueFromResponse(response.data, params.name)
            setFormValue(params.name, value)
          })
        }
      })
      .catch(() => {
        setProcessing(0)
      })
  }, [])

  const onSubmit: SubmitHandler<any> = (params, event) => {
    setProcessing(1)

    if ('polygon' in params) {
      params.polygon = JSON.parse(params.polygon)

      const typeCoordinates = params.polygon?.type === 'Polygon'
      const lengthCoordinates = params.polygon?.coordinates[0].length < 4

      if (!typeCoordinates || lengthCoordinates) {
        notify({
          type: 'error',
          message: `${
            !typeCoordinates
              ? 'Coordenadas não são do tipo Polígono'
              : 'Coordenadas precisam ter no mínimo 4 pontos'
          }`,
        })
        setProcessing(0)
        return false
      }
    }
    // Envia inspection_items apenas com o campo required
    if (model === 'inspection-config' && params.inspection_items) {
      const itemsArray = Array.isArray(params.inspection_items)
        ? params.inspection_items
        : [params.inspection_items]

      const mapped = itemsArray.map((it: any) => ({
        required: it.required ? 1 : 0,
      }))

      // Se for um único item, envie só o objeto { required: 1 }
      if (mapped.length === 1) {
        authFetch({
          url: `/${model}/${params.id}`,
          method: 'PUT',
          data: objectToSnakeCase(mapped[0]),
        })
          .then((response) => {
            if (response.data) {
              response.data.message
                ? notify({
                    message: `${translate(response.data.message)}`,
                    type: 'success',
                  })
                : notify({
                    message: `${translate(messageSuccess)}`,
                    type: 'success',
                  })
              setTimeout(handlePushToIndex, 1000)
              setProcessing(0)
            }
          })
          .finally(() => {
            setProcessing(0)
          })
        return
      }

      // Se for múltiplos, envia como array em inspection_items
      const payload = {
        inspection_items: mapped,
      }

      authFetch({
        url: `/${model}/${params.id}`,
        method: 'PUT',
        data: objectToSnakeCase(payload),
      })
        .then((response) => {
          if (response.data) {
            response.data.message
              ? notify({
                  message: `${translate(response.data.message)}`,
                  type: 'success',
                })
              : notify({
                  message: `${translate(messageSuccess)}`,
                  type: 'success',
                })
            setTimeout(handlePushToIndex, 1000)
            setProcessing(0)
          }
        })
        .finally(() => {
          setProcessing(0)
        })
      return
    }

    authFetch({
      url: `/${model}/${params.id}`,
      method: 'PUT',
      data: objectToSnakeCase(params),
    })
      .then((response) => {
        if (response.data) {
          response.data.message
            ? notify({
                message: `${translate(response.data.message)}`,
                type: 'success',
              })
            : notify({
                message: `${translate(messageSuccess)}`,
                type: 'success',
              })
          setTimeout(handlePushToIndex, 1000)
          setProcessing(0)
        }
      })
      .finally(() => {
        setProcessing(0)
      })
  }

  const handlePushToIndex = () => {
    history.push(`/${model}`)
  }

  return (
    <Layout marginLayout>
      <InternalHeader title={translate(title)} />

      <InternalContent expandButton={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <CrudGeneratorFields
              getValues={getValues}
              fields={fields}
              register={register}
              control={control}
              setFormValue={setFormValue}
              watch={watch}
              resetField={resetField}
            />
          </Grid>

          <CardActions sx={{ justifyContent: 'flex-end' }}>
            <ButtonForm type="cancel" onClick={handlePushToIndex}>
              {translate('Cancel')}
            </ButtonForm>
            <ButtonForm type="submit">
              <LoopIcon
                className="load-icon"
                sx={{ display: processing ? 'block' : 'none' }}
              />
              {processing ? translate('Saving') : translate('Save')}
            </ButtonForm>
          </CardActions>
        </form>
      </InternalContent>
    </Layout>
  )
}
